/**
 * WorkspacePicker (iter 72) — Post-login full-screen picker that separates
 * Personal from Business. Uses the existing `entities.workspace_type` field
 * under the hood — each entity is a fully-isolated set of books, so choosing
 * Personal here can never leak into a Business ledger and vice versa.
 *
 * Flow:
 *   • If the user has entities of both kinds → show the two big cards
 *   • Only one kind exists → auto-select the first of that kind + skip picker
 *   • Zero of the chosen kind → create a fresh entity of that kind, then enter
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, Briefcase, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useEntity } from "@/context/EntityContext";
import api from "@/lib/api";

export default function WorkspacePicker() {
  const { user } = useAuth();
  const { entities, setEntity, refresh } = useEntity();
  const navigate = useNavigate();
  const location = useLocation();
  // When the user reached this page via Switch Workspace, we do NOT auto-skip
  // even if only one kind of entity exists — they explicitly asked to pick.
  const isForced = new URLSearchParams(location.search).get("force") === "1";
  const [busy, setBusy] = useState(null); // "personal" | "business" | null
  // Guard against the auto-skip effect racing a manual click when the user's
  // only entity is a legacy (workspace_type=null) doc — the memo classifies
  // it as business and the effect would silently fire enter("business")
  // alongside the click.
  const enterOnce = useRef(false);

  const [personal, business] = useMemo(() => [
    entities.filter((e) => e.workspace_type === "personal"),
    // Legacy entities (workspace_type=null before iter 72) fall into Business
    // by default because the Business edition hides nothing.
    entities.filter((e) => e.workspace_type === "business" || e.workspace_type == null),
  ], [entities]);

  const enter = useCallback(async (kind) => {
    if (enterOnce.current) return;
    enterOnce.current = true;
    setBusy(kind);
    try {
      // iter 72c / 72u — always call ensure-workspace-pair so the user
      // always has exactly ONE personal + ONE business entity with strictly
      // isolated books. Pass the clicked kind so a fresh user who chose
      // "Business" gets the legacy default entity re-tagged as Business
      // (instead of always defaulting to Personal-first).
      const { data } = await api.post("/entities/ensure-workspace-pair", { preferred: kind });
      const target = kind === "personal" ? data.personal : data.business;
      await refresh();
      // Remember the kind for this browser session so we don't re-show the
      // picker on the next page load until logout.
      try { sessionStorage.setItem("ledger.workspace_kind", kind); } catch { /* private mode */ }
      setEntity(target.id);
      navigate("/", { replace: true });
    } catch (err) {
      console.warn("[WorkspacePicker] enter failed:", err?.response?.status, err?.message);
      toast.error("Couldn't open that workspace — please try again");
      enterOnce.current = false;
      setBusy(null);
    }
  }, [setEntity, refresh, navigate]);

  // Auto-skip when only one kind exists — no reason to force a click. Only
  // skip when the sole entity has an EXPLICIT workspace_type (never for the
  // legacy null-workspace default entity from registration), and never when
  // the user reached this page via Switch Workspace (`?force=1`).
  useEffect(() => {
    if (isForced || enterOnce.current || !entities?.length) return;
    const personalExplicit = entities.filter((e) => e.workspace_type === "personal");
    const businessExplicit = entities.filter((e) => e.workspace_type === "business");
    if (personalExplicit.length > 0 && businessExplicit.length === 0) enter("personal");
    else if (businessExplicit.length > 0 && personalExplicit.length === 0) enter("business");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities.length, isForced]);

  if (!user) {
    navigate("/login", { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--surface)] flex flex-col items-center justify-center p-6"
         data-testid="workspace-picker">
      <div className="max-w-3xl w-full">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-semibold text-[var(--text)] mb-3">
            Welcome back{user.name ? `, ${user.name.split(" ")[0]}` : ""}
          </h1>
          <p className="text-base text-[var(--muted-text)]">
            Which set of books do you want to work with today?
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <WorkspaceCard
            kind="personal"
            title="Personal"
            subtitle="Wallets, budgets, savings, investments"
            icon={<Home className="w-12 h-12" />}
            entities={personal}
            busy={busy === "personal"}
            onClick={() => enter("personal")}
          />
          <WorkspaceCard
            kind="business"
            title="Business"
            subtitle="Invoices, bills, payroll, financial reports"
            icon={<Briefcase className="w-12 h-12" />}
            entities={business}
            busy={busy === "business"}
            onClick={() => enter("business")}
          />
        </div>

        <p className="text-center text-xs text-[var(--muted-text)] mt-8">
          Each workspace keeps its own books — nothing is ever consolidated between them.
        </p>
      </div>
    </div>
  );
}

function WorkspaceCard({ kind, title, subtitle, icon, entities, busy, onClick }) {
  const hasSome = entities.length > 0;
  const accent = kind === "business" ? "var(--brand)" : "#8B4B18";
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={`workspace-${kind}-card`}
      className="group text-left border border-[var(--divider)] rounded-2xl bg-[var(--card)] p-8 hover:border-[var(--brand)] hover:shadow-lg transition-all disabled:opacity-60"
      style={{ borderTopColor: accent, borderTopWidth: 4 }}
    >
      <div className="flex items-start justify-between mb-6">
        <div style={{ color: accent }}>{icon}</div>
        {busy && <Loader2 className="w-5 h-5 animate-spin text-[var(--muted-text)]" />}
      </div>
      <h2 className="text-2xl font-semibold text-[var(--text)] mb-1">{title}</h2>
      <p className="text-sm text-[var(--muted-text)] mb-5">{subtitle}</p>
      {hasSome ? (
        <div className="text-xs text-[var(--muted-text)]">
          {entities.length === 1
            ? <>Enter <span className="font-medium text-[var(--text)]">{entities[0].name}</span></>
            : <>{entities.length} workspaces available</>}
        </div>
      ) : (
        <div className="text-xs text-[var(--muted-text)]">
          Create a fresh {title.toLowerCase()} workspace
        </div>
      )}
      {/* Visual affordance only — the outer <button> owns the click. Using
          a <span> avoids the nested-button hydration warning. */}
      <div className="mt-6">
        <span
          data-testid={`workspace-${kind}-enter`}
          className="inline-flex items-center h-9 px-3 rounded-md text-sm font-medium text-[var(--muted-text)] group-hover:bg-[var(--brand-light)] group-hover:text-[var(--brand-text)] transition-colors"
        >
          {hasSome ? "Continue →" : "Get started →"}
        </span>
      </div>
    </button>
  );
}
